import { FormEvent, useState } from "react";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { useEmailChange } from "../../utils/hooks/useEmailChange";
import { useErrorToast, useSuccessToast } from "../../utils/showToast";
import { useOrders } from "../../utils/hooks/useOrders";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { CONFIRM_TEXT } from "../../utils/constants/i18n-strings";
import {
  Card,
  CardContent,
  CardHeader,
  Collapsible,
  CollapsibleContent,
  Heading,
  IconButton,
  Input,
  Label,
  Text,
  VStack,
  Button,
  Callout,
} from "@wootingkb/component-library";
import { useBoolean } from "../../utils/hooks/useBoolean";
import { ReplaceableKey } from "../../lib/helper/stringReplacer";
import RegionawareLink from "../Common/RegionawareLink";

const EMAIL_CHANGE_SUCCESS_TOAST = msg({
  id: "User_email_change_success_toast",
  message:
    "Email change requested. Please check your new email for confirmation.",
});

const TOGGLE_EMAIL_ARIA_LABEL = msg({
  id: "User_toggle_change_email_aria_label",
  message: "Toggle change email dropdown",
});

export default function ChangeEmail() {
  const { requestEmailChange } = useEmailChange(false);
  const showErrorToast = useErrorToast();
  const showSuccessToast = useSuccessToast();
  const [flag, setFlag] = useBoolean();
  const [password, setPassword] = useState("");
  const [oldEmail, setOldEmail] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const orders = useOrders();
  const tooManyOrders = orders.orders && orders.orders.length >= 100;
  const { _: i18n } = useLingui();

  function resetForm() {
    setPassword("");
    setOldEmail("");
    setNewEmail("");
  }

  async function changeEmail(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setIsLoading(true);
    requestEmailChange(password, oldEmail, newEmail)
      .then(() => {
        showSuccessToast(i18n(EMAIL_CHANGE_SUCCESS_TOAST));
      })
      .catch((e) => {
        showErrorToast(e);
      })
      .finally(() => {
        resetForm();
        setIsLoading(false);
      });
  }

  return (
    <Card>
      <CardHeader className="flex-row justify-between">
        <Heading as="h3" size="h4" weight="semibold">
          <Trans id="User_change_email_title">Change Email</Trans>
        </Heading>
        <IconButton
          variant="muted"
          aria-label={i18n(TOGGLE_EMAIL_ARIA_LABEL)}
          onClick={setFlag.toggle}
          className={tooManyOrders ? "hidden" : ""}
        >
          {flag ? <BsChevronUp /> : <BsChevronDown />}
        </IconButton>
      </CardHeader>
      <CardContent>
        {tooManyOrders && (
          <Text>
            <Trans id="User_change_email_contact_support">
              Due to the large number of orders on your account, you will need
              to contact support to change your email address. Please reach out
              to us via our{" "}
              <RegionawareLink
                href={`${ReplaceableKey.WootingStoreURL}/support?issue=60`}
                variant="underline"
                external
                className="text-foreground-accent-primary"
              >
                Support Wizard
              </RegionawareLink>
              .
            </Trans>
          </Text>
        )}
        {!tooManyOrders && (
          <Collapsible
            open={flag}
            onOpenChange={(open) => (open ? setFlag.on() : setFlag.off())}
          >
            <CollapsibleContent className="flex flex-col gap-4">
              <Callout
                className="w-full"
                status="info"
                heading={
                  <Trans id="User_change_email_processing_note_title">
                    Note
                  </Trans>
                }
                description={
                  <Trans id="User_change_email_processing_note">
                    Changing email on an account with a large number of orders
                    may take a while to process. Please allow up to 24 hours for
                    all orders to be shown on your account after changing your
                    email address.
                  </Trans>
                }
              />
              <form onSubmit={changeEmail} className="flex flex-col gap-4 px-1">
                <VStack className="gap-1">
                  <Label htmlFor="current-password" weight="semibold" required>
                    <Trans id="User_change_email_password_label">
                      Password
                    </Trans>
                  </Label>
                  <Input
                    type="password"
                    name="current-password"
                    id="current-password"
                    required
                    onChange={(e) => setPassword(e.target.value)}
                    value={password}
                    autoComplete="current-password"
                  />
                </VStack>
                <VStack className="gap-1">
                  <Label htmlFor="current-email" weight="semibold" required>
                    <Trans id="User_change_email_current_email_label">
                      Current Email
                    </Trans>
                  </Label>
                  <Input
                    type="email"
                    name="current-email"
                    id="current-email"
                    required
                    onChange={(e) => setOldEmail(e.target.value)}
                    value={oldEmail}
                    autoComplete="email"
                  />
                </VStack>
                <VStack className="gap-1">
                  <Label htmlFor="new-email" weight="semibold" required>
                    <Trans id="User_change_email_new_email_label">
                      New Email
                    </Trans>
                  </Label>
                  <Input
                    type="email"
                    name="new-email"
                    id="new-email"
                    required
                    onChange={(e) => setNewEmail(e.target.value)}
                    value={newEmail}
                    autoComplete="email"
                  />
                </VStack>
                <Button
                  type="submit"
                  variant="secondary"
                  size="sm"
                  isLoading={isLoading}
                  className="self-end"
                >
                  {i18n(CONFIRM_TEXT)}
                </Button>
              </form>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
}
